/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import dayjs from "dayjs";
import { FlexContainer, Text, theme } from "@nordcloud/gnui";
import { useEventsQuery } from "~/generated/graphql";
import { ReactTable, UniversalWrap, TableLoader } from "~/components";
import { useBillingPeriod } from "~/services/customers";
import { ExternalLink } from "../ExternalLink";
import { DataDisplayWrapper } from "../NoData";
import { EventsListColumns } from "./EventsList";

type Props = {
  nid: string;
};

export function Events({ nid }: Props) {
  const { isCurrentBillingPeriod } = useBillingPeriod();

  const today = dayjs().format();
  const startOfCurrentMonth = dayjs().startOf("M").format();
  const endOfPreviousMonth = dayjs()
    .subtract(1, "month")
    .endOf("month")
    .format();
  const startOfPreviousMonth = dayjs()
    .startOf("M")
    .subtract(1, "month")
    .format();

  const to = isCurrentBillingPeriod ? today : endOfPreviousMonth;
  const from = isCurrentBillingPeriod
    ? startOfCurrentMonth
    : startOfPreviousMonth;

  const { data, error, loading } = useEventsQuery({
    variables: { nid, to, from },
  });

  const events = data?.events ?? [];
  const hasEvents = events.length > 0;

  const columns = useMemo(() => EventsListColumns, []);

  return (
    <UniversalWrap
      errorProps={{ error }}
      loaderProps={{ loading, Component: Loader }}
    >
      <DataDisplayWrapper
        hasData={hasEvents}
        messageComponent={<NoEventsData />}
      >
        <ReactTable striped columns={columns} data={data?.events || []} />
      </DataDisplayWrapper>
    </UniversalWrap>
  );
}

function NoEventsData() {
  return (
    <FlexContainer direction="column">
      <Text css={{ fontWeight: theme.fontWeights.bold }} mb="0">
        There are no events available
      </Text>
      <Text>
        You can create your own integration and push information about events
        here using our API
      </Text>
      <ExternalLink href="https://docs.mca.nordcloudapp.com/docs/operations/external-integrations/custom-events/create-events#add-event-using-graphql">
        Adding an event using GraphQL
      </ExternalLink>
      <ExternalLink href="https://docs.mca.nordcloudapp.com/docs/operations/external-integrations/custom-events/create-events#add-event-using-curl">
        Adding an event using cURL
      </ExternalLink>
    </FlexContainer>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={0}
      rows={[
        {
          count: 6,
          height: 60,
          items: [
            {
              widthPercent: 100,
            },
          ],
        },
      ]}
    />
  );
}
